import React, { useState, useEffect } from 'react';
import { primaryButtonSx, pageTabsSx } from '../../styles/adminPageStyles';
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Paper,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Button,
  Chip as MuiChip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Toolbar,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Badge,
  Alert,
  Chip,
  useTheme,
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Autorenew as AutorenewIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  Done as DoneIcon,
  Close as CloseIcon,
  FilterList as FilterListIcon,
  Assessment as AssessmentIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import CheckResponses from './CheckResponses';
import EmailWebSubmissionsPanel from './EmailWebSubmissionsPanel';
import PageHeader from '../../components/PageHeader';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';
import { useSearchParams } from 'react-router-dom';
import { resolveAdminUiLabel } from '../../constants/adminUiLabels';
import { useAdminUiLabels } from '../../context/AdminUiLabelsContext';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

// Add interface for check statistics
interface CheckStatistics {
  totalChecks: number;
  completed: number;
  inProgress: number;
  notStarted: number;
  loading: boolean;
}

// Statistics Card Component - dashboard-style (rounded icon box, hover lift)
interface StatisticsCardProps {
  title: string;
  count: React.ReactNode;
  icon: React.ReactNode;
  color: 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning';
}

const StatisticsCard: React.FC<StatisticsCardProps> = ({ title, count, icon, color }) => {
  const theme = useTheme();
  const colorValue = theme.palette[color].main;
  return (
    <Card
      sx={{
        height: '100%',
        borderRadius: 4,
        boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 20px 45px rgba(0,0,0,0.12)',
        },
      }}
    >
      <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2, '&:last-child': { pb: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '16px',
              backgroundColor: `${colorValue}22`,
              color: colorValue,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {React.cloneElement(icon as React.ReactElement, { fontSize: 'large' })}
          </Box>
          <Box>
            <Typography variant="overline" color="text.secondary">{title}</Typography>
            <Typography variant="h4" sx={{ fontWeight: 800 }}>{count}</Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`checks-tabpanel-${index}`}
      aria-labelledby={`checks-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box>
          {children}
        </Box>
      )}
    </div>
  );
}

const SAFETY_CHECK_TABS = ['overview', 'responses', 'email-web'] as const;

const tabSlugToIndex = (tabParam: string | null, safetyCheckIdParam: string | null): number => {
  if (safetyCheckIdParam || tabParam === 'responses') return 1;
  if (tabParam === 'templates') return 1;
  if (tabParam === 'email-web') return 2;
  if (tabParam === 'overview' || tabParam === 'overdue-frequency') return 0;
  return 0;
};

const Checks: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { adminUiLabels } = useAdminUiLabels();
  const tabParam = searchParams.get('tab');
  const safetyCheckIdParam =
    searchParams.get('safety_check_id') || searchParams.get('focus');

  const [value, setValue] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return tabSlugToIndex(
      params.get('tab'),
      params.get('safety_check_id') || params.get('focus'),
    );
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [stats, setStats] = useState<CheckStatistics>({
    totalChecks: 0,
    completed: 0,
    inProgress: 0,
    notStarted: 0,
    loading: true
  });

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 500);
    fetchCheckStatistics();
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const idx = tabSlugToIndex(tabParam, safetyCheckIdParam);
    if (idx !== value) setValue(idx);
  }, [tabParam, safetyCheckIdParam, value]);

  const fetchCheckStatistics = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No authentication token found');
        return;
      }

      console.log('Fetching check statistics...');
      const response = await axios.get(`${API_BASE_URL}/safety-checks/stats/safety-overview`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('Safety overview statistics response:', response.data);

      if (response.data?.success) {
        const d = response.data.data || {};
        const statsData = {
          totalChecks: d.total_checks || 0,
          completed: d.completed_checks || 0,
          inProgress: d.pending_checks || 0,
          notStarted: d.not_started_checks || 0,
          loading: false
        };
        console.log('Setting stats to:', statsData);
        setStats(statsData);
      } else {
        console.warn('Empty response from safety-overview endpoint');
        setStats(prev => ({ ...prev, loading: false }));
      }
    } catch (error) {
      console.error('Error fetching check statistics:', error);
      setStats(prev => ({ ...prev, loading: false }));
    } finally {
      setRefreshing(false);
    }
  };

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    const params = new URLSearchParams(searchParams);
    if (newValue === 0) {
      params.delete('tab');
    } else {
      params.set('tab', SAFETY_CHECK_TABS[newValue]);
    }
    setSearchParams(params, { replace: true });
  };

  // Overview platform notifications
  const [platformNotes, setPlatformNotes] = useState<any[]>([]);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [notesSearch, setNotesSearch] = useState('');
  const [notesPage, setNotesPage] = useState(1);
  const [notesLimit, setNotesLimit] = useState(10);
  const [readFilter, setReadFilter] = useState<'all' | 'read' | 'unread'>('all');
  const [aiAnalysisDialog, setAiAnalysisDialog] = useState<{
    open: boolean;
    safetyCheckId: string | null;
    loading: boolean;
    data: any;
    error: string | null;
  }>({
    open: false,
    safetyCheckId: null,
    loading: false,
    data: null,
    error: null,
  });

  const [messageDialog, setMessageDialog] = useState<{
    open: boolean;
    title: string;
    message: string;
    severity: string;
    created_at: string;
    notification_id: string;
    is_read: boolean;
  }>({
    open: false,
    title: '',
    message: '',
    severity: '',
    created_at: '',
    notification_id: '',
    is_read: false,
  });

  const fetchPlatformNotifications = async () => {
    try {
      setLoadingNotes(true);
      const token = localStorage.getItem('token');
      if (!token) return;
      const res = await axios.get(`${API_BASE_URL}/notifications/platform`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data?.success) setPlatformNotes(res.data.data || []);
    } catch (e) {
      console.error('Failed to load platform notifications', e);
      setPlatformNotes([]);
    } finally {
      setLoadingNotes(false);
    }
  };

  useEffect(() => {
    fetchPlatformNotifications();
  }, []);

  const handleMarkAsRead = async (notificationId: string) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      await axios.put(
        `${API_BASE_URL}/notifications/platform/${notificationId}/read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state
      setPlatformNotes(prev =>
        prev.map(n =>
          n.notification_id === notificationId
            ? { ...n, is_read: true }
            : n
        )
      );
      
      // Update dialog if it's open for this notification
      if (messageDialog.notification_id === notificationId) {
        setMessageDialog(prev => ({ ...prev, is_read: true }));
      }
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  };

  // Derived filtered + paginated notifications
  const filteredNotes = platformNotes.filter(n => {
    // Search filter
    if (notesSearch.trim()) {
      const q = notesSearch.toLowerCase();
      const matchesSearch = (
        String(n.title || '').toLowerCase().includes(q) ||
        String(n.message || '').toLowerCase().includes(q) ||
        String(n.severity || '').toLowerCase().includes(q)
      );
      if (!matchesSearch) return false;
    }
    
    // Read/unread filter
    if (readFilter === 'read') return n.is_read === true;
    if (readFilter === 'unread') return n.is_read === false;
    return true; // 'all'
  });
  
  const totalPages = Math.max(1, Math.ceil(filteredNotes.length / notesLimit));
  const page = Math.min(notesPage, totalPages);
  const startIdx = (page - 1) * notesLimit;
  const visibleNotes = filteredNotes.slice(startIdx, startIdx + notesLimit);

  // Sync Template Questions button moved to Templates screen

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', pt: 0.5, pb: 3 }}>
        {/* Enhanced header with statistics cards */}
        <PageHeader
          title={resolveAdminUiLabel('nav_safety_checks', adminUiLabels)}
          subtitle="Monitor safety checks across your organisation. Overview shows platform notifications relevant to safety."
          action={
            <Button
              variant="outlined"
              startIcon={refreshing ? <CircularProgress size={18} /> : <RefreshIcon />}
              onClick={() => { setRefreshing(true); fetchCheckStatistics(); }}
              disabled={refreshing}
              sx={primaryButtonSx}
            >
              {refreshing ? 'Refreshing…' : 'Refresh overview'}
            </Button>
          }
        />

        {/* Statistics Cards - dashboard style */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <StatisticsCard
                title="Total Checks"
                count={stats.loading ? <CircularProgress size={24} /> : stats.totalChecks || 0}
                icon={<AssignmentIcon />}
                color="primary"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatisticsCard
                title="Completed"
                count={stats.loading ? <CircularProgress size={24} /> : stats.completed || 0}
                icon={<CheckCircleIcon />}
                color="success"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatisticsCard
                title="In Progress"
                count={stats.loading ? <CircularProgress size={24} /> : stats.inProgress || 0}
                icon={<AutorenewIcon />}
                color="info"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatisticsCard
                title="Not Started"
                count={stats.loading ? <CircularProgress size={24} /> : stats.notStarted || 0}
                icon={<PendingIcon />}
                color="warning"
              />
            </Grid>
          </Grid>

        <Paper sx={{ width: '100%', borderRadius: 1.5, overflow: 'hidden', border: '1px solid rgba(0,0,0,0.12)', boxShadow: 'none' }}>
            <Tabs
              value={value}
              onChange={handleChange}
              aria-label="checks tabs"
              variant="fullWidth"
              sx={pageTabsSx}
            >
              <Tab label="Overview" />
              {/* Templates moved to main sidebar */}
              <Tab label="Responses" />
              <Tab label="Email (web)" />
            </Tabs>
          <TabPanel value={value} index={0}>
            <Box sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>Platform Notifications</Typography>
                  <Badge 
                    badgeContent={platformNotes.filter(n => !n.is_read).length} 
                    color="error"
                    sx={{ '& .MuiBadge-badge': { fontSize: '0.75rem', height: '20px', minWidth: '20px' } }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {platformNotes.length} total
                    </Typography>
                  </Badge>
                </Box>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={fetchPlatformNotifications}
                  disabled={loadingNotes}
                  size="small"
                >
                  Refresh
                </Button>
              </Box>
              
              <Toolbar disableGutters sx={{ mb: 2, gap: 2, flexWrap: 'wrap' }}>
                <TextField 
                  size="small" 
                  label="Search" 
                  value={notesSearch} 
                  onChange={(e) => { setNotesSearch(e.target.value); setNotesPage(1); }} 
                  sx={{ minWidth: 240 }} 
                />
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel><FilterListIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} /> Status</InputLabel>
                  <Select 
                    value={readFilter} 
                    label="Status" 
                    onChange={(e) => { setReadFilter(e.target.value as 'all' | 'read' | 'unread'); setNotesPage(1); }}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="unread">Unread</MenuItem>
                    <MenuItem value="read">Read</MenuItem>
                  </Select>
                </FormControl>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ ml: 'auto' }}>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Per Page</InputLabel>
                    <Select value={notesLimit} label="Per Page" onChange={(e) => { setNotesLimit(Number(e.target.value)); setNotesPage(1); }}>
                      <MenuItem value={10}>10</MenuItem>
                      <MenuItem value={25}>25</MenuItem>
                      <MenuItem value={50}>50</MenuItem>
                    </Select>
                  </FormControl>
                </Stack>
              </Toolbar>
              
              {loadingNotes ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : filteredNotes.length === 0 ? (
                <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
                  <Typography color="text.secondary">No notifications found.</Typography>
                </Paper>
              ) : (
                <>
                  <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Severity</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Title</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Message</TableCell>
                          <TableCell sx={{ fontWeight: 600 }} align="center">Status</TableCell>
                          <TableCell sx={{ fontWeight: 600 }} align="center">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {visibleNotes.map((n) => (
                          <TableRow 
                            key={n.notification_id} 
                            hover
                            sx={{ 
                              bgcolor: n.is_read ? 'transparent' : 'action.hover',
                              '&:hover': { bgcolor: 'action.hover' }
                            }}
                          >
                            <TableCell>
                              <Typography variant="body2">
                                {new Date(n.created_at).toLocaleString()}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {n.severity ? (
                                <MuiChip 
                                  size="small" 
                                  label={String(n.severity).toLowerCase()} 
                                  color={
                                    String(n.severity).toLowerCase() === 'critical' ? 'error' : 
                                    String(n.severity).toLowerCase() === 'high' ? 'warning' : 
                                    'default'
                                  }
                                  sx={{ fontWeight: 500 }}
                                />
                              ) : (
                                <MuiChip size="small" label="low" />
                              )}
                            </TableCell>
                            <TableCell>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  fontWeight: n.is_read ? 400 : 600,
                                  color: n.is_read ? 'text.secondary' : 'text.primary'
                                }}
                              >
                                {n.title}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography 
                                variant="body2" 
                                color="text.secondary" 
                                sx={{
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  maxWidth: 400,
                                }}
                              >
                                {n.message}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              {n.is_read ? (
                                <MuiChip 
                                  size="small" 
                                  label="Read" 
                                  color="default"
                                  variant="outlined"
                                />
                              ) : (
                                <MuiChip 
                                  size="small" 
                                  label="Unread" 
                                  color="primary"
                                />
                              )}
                            </TableCell>
                            <TableCell align="center">
                              <Stack direction="row" spacing={1} justifyContent="center">
                                {n.type === 'safety_check' && (() => {
                                  const extractSafetyCheckId = (actionUrl: string, title: string): string | null => {
                                    if (!actionUrl) return null;
                                    const patterns = [
                                      /\/safety-checks\/([^/?]+)/,
                                      /\/admin\/safety-checks\/analysis\/([^/?]+)/,
                                      /safety_check_id=([^&]+)/,
                                      /safetyCheckId=([^&]+)/,
                                      /check_id=([^&]+)/,
                                    ];
                                    for (const pattern of patterns) {
                                      const match = actionUrl.match(pattern);
                                      if (match) return match[1];
                                    }
                                    return null;
                                  };
                                  
                                  return (
                                    <Tooltip title="View AI Report">
                                      <IconButton
                                        size="small"
                                        onClick={async () => {
                                          let checkId = extractSafetyCheckId(n.action_url || '', n.title);
                                          
                                          if (!checkId) {
                                            try {
                                              const token = localStorage.getItem('token');
                                              const checkNameMatch = n.title.match(/Failed Safety Check: (.+)/);
                                              
                                              if (checkNameMatch) {
                                                const checkName = checkNameMatch[1].trim();
                                                try {
                                                  const responsesRes = await axios.get(
                                                    `${API_BASE_URL}/check-responses?check_status=FAIL,fail&limit=20`,
                                                    { headers: { Authorization: `Bearer ${token}` } }
                                                  );
                                                  
                                                  if (responsesRes.data?.success && responsesRes.data.data?.length > 0) {
                                                    const failedChecks = responsesRes.data.data;
                                                    const matchingCheck = failedChecks.find((check: any) => {
                                                      const checkNameFromData = check.check_name || '';
                                                      return checkNameFromData === checkName || 
                                                             checkNameFromData.includes(checkName) ||
                                                             checkName.includes(checkNameFromData);
                                                    });
                                                    
                                                    if (matchingCheck?.safety_check_id) {
                                                      checkId = matchingCheck.safety_check_id;
                                                    } else if (failedChecks.length > 0) {
                                                      const sortedChecks = failedChecks.sort((a: any, b: any) => {
                                                        const dateA = new Date(a.created_at || 0).getTime();
                                                        const dateB = new Date(b.created_at || 0).getTime();
                                                        return dateB - dateA;
                                                      });
                                                      checkId = sortedChecks[0]?.safety_check_id;
                                                    }
                                                  }
                                                } catch (err) {
                                                  console.error('Error querying check responses:', err);
                                                }
                                              }
                                            } catch (err) {
                                              console.error('Error finding safety check ID:', err);
                                            }
                                          }
                                          
                                          if (!checkId) {
                                            alert('Unable to find the safety check. Please navigate to Safety Checks to view the AI analysis.');
                                            return;
                                          }
                                          
                                          setAiAnalysisDialog({
                                            open: true,
                                            safetyCheckId: checkId,
                                            loading: true,
                                            data: null,
                                            error: null,
                                          });
                                          try {
                                            const token = localStorage.getItem('token');
                                            const res = await axios.get(
                                              `${API_BASE_URL}/check-responses/analysis/${checkId}`,
                                              { headers: { Authorization: `Bearer ${token}` } }
                                            );
                                            if (res.data?.success && res.data.data && res.data.data.length > 0) {
                                              const latestAnalysis = res.data.data[0];
                                              const analysisText = latestAnalysis.analysis_text || '';
                                              const isFail = analysisText.trim().split('\n')[0] === 'FAIL';
                                              setAiAnalysisDialog(prev => ({
                                                ...prev,
                                                loading: false,
                                                data: {
                                                  analysis_text: analysisText,
                                                  detailed_explanation: analysisText,
                                                  should_mark_out_of_action: isFail,
                                                  created_at: latestAnalysis.created_at,
                                                  analysis_id: latestAnalysis.analysis_id
                                                },
                                                error: null,
                                              }));
                                            } else {
                                              setAiAnalysisDialog(prev => ({
                                                ...prev,
                                                loading: false,
                                                error: 'No AI analysis found for this safety check',
                                              }));
                                            }
                                          } catch (e: any) {
                                            setAiAnalysisDialog(prev => ({
                                              ...prev,
                                              loading: false,
                                              error: e.response?.data?.message || 'Failed to fetch AI analysis',
                                            }));
                                          }
                                        }}
                                        sx={{ 
                                          color: 'text.secondary',
                                          '&:hover': { 
                                            color: 'primary.main',
                                            bgcolor: 'action.hover'
                                          }
                                        }}
                                      >
                                        <AssessmentIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  );
                                })()}
                                <Tooltip title="View Full Message">
                                  <IconButton
                                    size="small"
                                    onClick={() => setMessageDialog({
                                      open: true,
                                      title: n.title,
                                      message: n.message,
                                      severity: n.severity || 'low',
                                      created_at: n.created_at,
                                      notification_id: n.notification_id,
                                      is_read: n.is_read || false,
                                    })}
                                  >
                                    <VisibilityIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                {!n.is_read && (
                                  <Tooltip title="Mark as Read">
                                    <IconButton
                                      size="small"
                                      color="success"
                                      onClick={() => handleMarkAsRead(n.notification_id)}
                                    >
                                      <DoneIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Stack>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Paper>
                  
                  <Paper sx={{ p: 2, mt: 2, borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Showing {startIdx + 1} to {Math.min(startIdx + notesLimit, filteredNotes.length)} of {filteredNotes.length} results
                      </Typography>
                      <Pagination 
                        count={totalPages} 
                        page={page} 
                        onChange={(_, p) => setNotesPage(p)} 
                        color="primary" 
                        showFirstButton 
                        showLastButton 
                      />
                    </Box>
                  </Paper>
                </>
              )}
            </Box>
            
            {/* Full Message Dialog */}
            <Dialog
              open={messageDialog.open}
              onClose={() => setMessageDialog({ ...messageDialog, open: false })}
              maxWidth="md"
              fullWidth
              PaperProps={{
                sx: {
                  maxHeight: '90vh',
                  display: 'flex',
                  flexDirection: 'column'
                }
              }}
            >
              <DialogTitle sx={{ flexShrink: 0 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box sx={{ flex: 1, pr: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      {messageDialog.severity && (
                        <MuiChip 
                          size="small" 
                          label={String(messageDialog.severity).toLowerCase()} 
                          color={
                            String(messageDialog.severity).toLowerCase() === 'critical' ? 'error' : 
                            String(messageDialog.severity).toLowerCase() === 'high' ? 'warning' : 
                            'default'
                          }
                        />
                      )}
                      {!messageDialog.is_read && (
                        <MuiChip 
                          size="small" 
                          label="Unread" 
                          color="primary"
                        />
                      )}
                    </Box>
                    <Typography variant="h6" component="div">
                      {messageDialog.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      {new Date(messageDialog.created_at).toLocaleString()}
                    </Typography>
                  </Box>
                  <IconButton
                    aria-label="close"
                    onClick={() => setMessageDialog({ ...messageDialog, open: false })}
                    sx={{ color: (theme) => theme.palette.grey[500] }}
                  >
                    <CloseIcon />
                  </IconButton>
                </Box>
              </DialogTitle>
              <DialogContent 
                dividers
                sx={{
                  flex: '1 1 auto',
                  overflowY: 'auto',
                  minHeight: 0,
                  py: 2
                }}
              >
                <Typography 
                  variant="body1" 
                  sx={{ 
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    lineHeight: 1.6,
                    color: 'text.primary',
                    maxWidth: '100%'
                  }}
                >
                  {messageDialog.message}
                </Typography>
              </DialogContent>
              <DialogActions sx={{ px: 3, py: 2 }}>
                {!messageDialog.is_read && (
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<DoneIcon />}
                    onClick={() => {
                      handleMarkAsRead(messageDialog.notification_id);
                      setMessageDialog({ ...messageDialog, is_read: true });
                    }}
                  >
                    Mark as Read
                  </Button>
                )}
                <Button
                  onClick={() => setMessageDialog({ ...messageDialog, open: false })}
                  variant="outlined"
                >
                  Close
                </Button>
              </DialogActions>
            </Dialog>

            {/* AI Analysis Dialog */}
            <Dialog
              open={aiAnalysisDialog.open}
              onClose={() => setAiAnalysisDialog({ open: false, safetyCheckId: null, loading: false, data: null, error: null })}
              maxWidth="md"
              fullWidth
              PaperProps={{
                sx: {
                  maxHeight: '90vh',
                  display: 'flex',
                  flexDirection: 'column'
                }
              }}
            >
              <DialogTitle sx={{ flexShrink: 0, borderBottom: 1, borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    AI Analysis
                  </Typography>
                  <IconButton
                    aria-label="close"
                    onClick={() => setAiAnalysisDialog({ open: false, safetyCheckId: null, loading: false, data: null, error: null })}
                    sx={{ color: (theme) => theme.palette.grey[500] }}
                  >
                    <CloseIcon />
                  </IconButton>
                </Box>
              </DialogTitle>
              <DialogContent 
                dividers
                sx={{
                  flex: '1 1 auto',
                  overflowY: 'auto',
                  minHeight: 0,
                  py: 3
                }}
              >
                {aiAnalysisDialog.loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                    <CircularProgress />
                  </Box>
                ) : aiAnalysisDialog.error ? (
                  <Alert severity="error">{aiAnalysisDialog.error}</Alert>
                ) : aiAnalysisDialog.data ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {/* Status Summary */}
                    <Box sx={{ 
                      p: 2.5, 
                      bgcolor: aiAnalysisDialog.data.should_mark_out_of_action ? '#fff5f5' : '#f1f8f4', 
                      borderRadius: 2,
                      border: '2px solid',
                      borderColor: aiAnalysisDialog.data.should_mark_out_of_action ? 'error.main' : 'success.main'
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5, flexWrap: 'wrap' }}>
                        <Chip
                          label={aiAnalysisDialog.data.should_mark_out_of_action ? 'FAIL' : 'PASS'}
                          color={aiAnalysisDialog.data.should_mark_out_of_action ? 'error' : 'success'}
                          sx={{ fontWeight: 'bold', fontSize: '0.875rem', height: '32px' }}
                        />
                      </Box>
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                        Analysis Date: {new Date(aiAnalysisDialog.data.created_at).toLocaleString()}
                      </Typography>
                    </Box>

                    {/* Detailed Explanation */}
                    {(aiAnalysisDialog.data.detailed_explanation || aiAnalysisDialog.data.analysis_text) && (
                      <Box sx={{ 
                        p: 2.5, 
                        borderRadius: 2,
                        bgcolor: 'background.paper',
                        border: '1px solid',
                        borderColor: 'divider'
                      }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, color: 'text.primary' }}>
                          Detailed Analysis
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            lineHeight: 1.8,
                            color: 'text.primary'
                          }}
                        >
                          {aiAnalysisDialog.data.detailed_explanation || aiAnalysisDialog.data.analysis_text}
                        </Typography>
                      </Box>
                    )}

                    {!aiAnalysisDialog.data.detailed_explanation && !aiAnalysisDialog.data.analysis_text && (
                      <Alert severity="info">
                        No detailed analysis available for this safety check.
                      </Alert>
                    )}
                  </Box>
                ) : null}
                {aiAnalysisDialog.safetyCheckId && (
                  <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                    <Tooltip title="Copy check ID for support" arrow placement="top">
                      <Box
                        component="span"
                        onClick={async () => {
                          try {
                            await navigator.clipboard.writeText(aiAnalysisDialog.safetyCheckId || '');
                          } catch { /* ignore */ }
                        }}
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.5,
                          fontSize: '0.7rem',
                          fontFamily: 'monospace',
                          color: 'text.disabled',
                          cursor: 'pointer',
                          '&:hover': { color: 'text.secondary' },
                        }}
                      >
                        <ContentCopyIcon sx={{ fontSize: 12, opacity: 0.7 }} />
                        <Box component="span">Check ID: {aiAnalysisDialog.safetyCheckId}</Box>
                      </Box>
                    </Tooltip>
                  </Box>
                )}
              </DialogContent>
              <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
                <Button
                  onClick={() => setAiAnalysisDialog({ open: false, safetyCheckId: null, loading: false, data: null, error: null })}
                  variant="outlined"
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  Close
                </Button>
              </DialogActions>
            </Dialog>
          </TabPanel>
          <TabPanel value={value} index={1}>
            <CheckResponses
              extraQuery={{
                check_type: 'SAFETY,LOAN,LOAN_RETURN',
                check_status: 'completed,FAIL,fail',
                ...(safetyCheckIdParam ? { safety_check_id: safetyCheckIdParam } : {})
              }}
            />
          </TabPanel>
          <TabPanel value={value} index={2}>
            <EmailWebSubmissionsPanel />
          </TabPanel>
        </Paper>
    </Box>
  );
};

export default Checks;